import { ChangeEvent, useEffect, useMemo, useState } from 'react'

import cn from 'classnames'
import { InfoIcon, Loader2 } from 'lucide-react'
import Skeleton from 'react-loading-skeleton'
import { Tooltip } from 'react-tooltip'

import { getTokenMetadata } from '@src/api/solana.api'
import MilesTracker from '@src/components/MilesTracker'
import SandDollarsTracker from '@src/components/SandDollarsTracker'
import Toggle from '@src/components/Toggle'
import {
    ChainId,
    DEFAULT_NETWORK_ICONS,
    getChainName,
    getChainNameFromChainId,
    getChainType,
} from '@src/configs/chainInfo'
import { copy } from '@src/constants/application'
import { MAX_TOKEN_DISPLAY_DECIMALS, SOLANA_TOKEN_ACCOUNT_TOOLTIP } from '@src/constants/exchange'
import { useBitcoinPrice } from '@src/hooks/useBitcoinPrice'
import { DestinationWalletInput } from '@src/pages/Exchange/components/DestinationWalletInput'
import RefreshButton from '@src/pages/Exchange/components/Refresh'
import TokenSelectorModal from '@src/pages/Exchange/components/TokenSelectorModal'
import { useExchangeState } from '@src/pages/Exchange/context/ExchangeStateContext'
import useExchangeBalances from '@src/pages/Exchange/hooks/useExchangeBalances'
import { useExchangeDerivedState } from '@src/pages/Exchange/hooks/useExchangeDerivedState'
import { IExchangeData } from '@src/types/exchange'
import { ISandDollarsData } from '@src/types/order'
import { Address, IToken } from '@src/types/token'
import { formatNumberStringWithCommas, isFeatureEnabled } from '@src/utils'
import { Balance, computeUSDAmount } from '@src/utils/balance'
import { computeBitcoinUSDAmount, isBitcoinAsset } from '@src/utils/bitcoinUtils'
import { isFeatherChain } from '@src/utils/chainUtils'
import { isUnWrapOnly, isWrapOnly } from '@src/utils/exchangeUtils'
import { useConfigStore } from '@src/zustand_stores'

// Custom hook to detect mobile-custom breakpoint (980px)
function useMediaQuery(query: string): boolean {
    const [matches, setMatches] = useState(false)

    useEffect(() => {
        const media = window.matchMedia(query)
        if (media.matches !== matches) {
            setMatches(media.matches)
        }
        const listener = () => setMatches(media.matches)
        media.addEventListener('change', listener)
        return () => media.removeEventListener('change', listener)
    }, [matches, query])

    return matches
}

interface IToSectionProps {
    buyData: IExchangeData
    sellData: IExchangeData // if you need it for cross-chain logic

    isCustomRecipientWallet: boolean
    isCrossChain: boolean // eslint-disable-line @typescript-eslint/no-unused-vars
    onToggleCustomWallet: () => void

    destinationAddress: Address | undefined
    validateAddress: (address: Address | undefined, chainId: number) => boolean
    onChangeDestAddress: (event: ChangeEvent<HTMLInputElement>) => void

    // SNS resolution props (optional)
    isResolvingSNS?: boolean
    resolvedSNSAddress?: string | null

    // ENS resolution props (optional)
    isResolvingENS?: boolean
    resolvedENSAddress?: string | null

    /** The read-only text for the "to" amount if you want to display it. */
    buyAmountString: string
    isBuyOutputLoading: boolean
    isQuoteLoading: boolean
    isCheckingLiquidity: boolean
    liquidityAvailable?: boolean
    isManuallyRefreshing?: boolean
    isQuoteEnabled?: boolean
    isWrapOrUnwrapOnly?: boolean

    // Tracker props
    miles?: number
    sandDollars?: ISandDollarsData
    hasErrors?: boolean

    onTokenSelectionChange: (
        newData: { token?: IToken; chainId?: number; value?: bigint },
        type: 'buy' | 'sell'
    ) => void
    onClickAmount?: () => void // e.g. for copying the final buy amount
    handleManualRefreshWrapper: () => void // for the refresh button
}

export function ToSection({
    buyData,
    sellData,
    isCustomRecipientWallet,
    isCrossChain,
    onToggleCustomWallet,
    destinationAddress,
    validateAddress,
    onChangeDestAddress,
    buyAmountString,
    isBuyOutputLoading,
    isQuoteLoading,
    onTokenSelectionChange,
    onClickAmount,
    isCheckingLiquidity,
    liquidityAvailable = true,
    isResolvingSNS,
    resolvedSNSAddress,
    isResolvingENS,
    resolvedENSAddress,
    isManuallyRefreshing,
    isQuoteEnabled,
    isWrapOrUnwrapOnly,
    handleManualRefreshWrapper,
    miles = 0,
    sandDollars,
    hasErrors = false,
}: IToSectionProps) {
    const { isBuyBalanceLoading, buyBalance, buyBalanceStabilized } = useExchangeBalances()
    const { isQuoteEnabled: exchangeQuoteEnabled } = useExchangeState()
    const { disabledToChains: disabledChains } = useExchangeDerivedState()
    const availableNetworks = useConfigStore(config => config.availableNetworks)
    const configLoading = useConfigStore(config => config.loading)
    const [isTokenSelectorModalOpen, setIsTokenSelectorModalOpen] = useState(false)

    // Detect mobile-custom breakpoint (980px and above)
    const isWiderThanMobile = useMediaQuery('(min-width: 980px)')

    // Check if both sides are stablecoins
    const isStablecoinSwap = sellData.token.isStableCoin && buyData.token.isStableCoin
    const isSandDollarsZero = !sandDollars || parseFloat(sandDollars.amount) === 0
    const shouldShowStablecoinMessage = isStablecoinSwap && isSandDollarsZero

    // For loading state of tokens
    const isConfigurationLoading = useMemo(() => {
        return configLoading === 'loading_not_yet_started' || configLoading === 'loading'
    }, [configLoading])

    // Get Bitcoin price for Bitcoin assets
    const { price: bitcoinPrice, loading: bitcoinPriceLoading } = useBitcoinPrice()

    const buyBalanceInUSD = useMemo<string | undefined>(() => {
        if (isBuyOutputLoading) {
            return undefined
        }

        // For Bitcoin assets, use our custom implementation
        if (isBitcoinAsset(buyData.token)) {
            const btcUsdAmount = computeBitcoinUSDAmount(buyData.value, bitcoinPrice, buyData.token)
            return btcUsdAmount ? `$${formatNumberStringWithCommas(btcUsdAmount)}` : undefined
        }

        // Regular token handling
        return `$${formatNumberStringWithCommas(computeUSDAmount(buyData.token, buyData.value)?.toString())}`
    }, [buyData.token, buyData.value, isBuyOutputLoading, bitcoinPrice])

    const isDestinationSolana = buyData.chainId === ChainId.Solana

    useEffect(() => {
        if (buyAmountString && buyAmountString !== '0') {
            console.log('ToSection: Buy amount display updated:', {
                buyAmountString,
                isBuyOutputLoading,
                isQuoteLoading,
                buyDataValue: buyData.value.toString(),
            })
        }
    }, [buyAmountString, buyData.value, isBuyOutputLoading, isQuoteLoading])

    return (
        <div className='space-y-2 rounded-[10px] border-mach-surface-border bg-mach-surface-background p-5'>
            {/* Header row with Receive label and balance */}
            <div className='flex items-center justify-between'>
                <div className='flex items-center gap-2'>
                    <span className='text-fluid-lg font-bold !leading-5 text-white'>Receive</span>
                    <div
                        className='transition-opacity duration-300'
                        style={{
                            opacity:
                                !sellData.value ||
                                sellData.value === BigInt(0) ||
                                sellData.value.toString() === '0' ||
                                sellData.value.toString() === '.'
                                    ? '0'
                                    : '1',
                        }}
                    >
                        <RefreshButton
                            disabled={
                                !isQuoteEnabled || !sellData.value || Number(sellData.value) === 0 || isWrapOrUnwrapOnly
                            }
                            isLoading={
                                (liquidityAvailable &&
                                    isQuoteEnabled &&
                                    (isBuyOutputLoading || isManuallyRefreshing || isQuoteLoading)) ||
                                isCheckingLiquidity
                            }
                            onClick={handleManualRefreshWrapper}
                        />
                    </div>
                </div>

                <div className='flex items-center gap-4'>
                    {buyBalance && (
                        <span className='whitespace-nowrap text-fluid-sm !leading-5 text-mach-sub-text'>
                            {isBuyBalanceLoading ? (
                                <span className='inline-flex items-center gap-1'>
                                    <Loader2 className='h-3 w-3 animate-spin' />
                                    {copy.balanceLabel}:
                                </span>
                            ) : (
                                `${copy.balanceLabel}:`
                            )}{' '}
                            {!isBuyBalanceLoading && buyBalanceStabilized
                                ? `${buyBalance ? buyBalance.formatWithMagnitudes(MAX_TOKEN_DISPLAY_DECIMALS) : 0} ${buyData.token.symbol}`
                                : '—'}
                        </span>
                    )}
                </div>
            </div>

            {/* Input and token selection */}
            <div className='flex items-start'>
                {/* Input field */}
                <div className='flex flex-1 flex-col gap-y-2'>
                    <input
                        className={cn(
                            'w-full bg-transparent text-left text-fluid-4xl font-semibold !leading-[44px]',
                            // if it's not loading & has a valid output, make it clickable
                            !isBuyOutputLoading && !isQuoteLoading && buyAmountString !== '0' && buyAmountString !== ''
                                ? 'cursor-pointer hover:opacity-80'
                                : 'cursor-default'
                        )}
                        placeholder={
                            // Show Loading... when checking liquidity
                            isCheckingLiquidity
                                ? 'Loading...'
                                : // For wrap/unwrap scenarios, don't show loading state even if isQuoteLoading is true
                                  // Also don't show loading if liquidity is not available
                                  (isBuyOutputLoading || isQuoteLoading) &&
                                    exchangeQuoteEnabled &&
                                    !isWrapOnly(sellData, buyData) &&
                                    !isUnWrapOnly(sellData, buyData) &&
                                    liquidityAvailable
                                  ? sellData.value > 0
                                      ? 'Loading...'
                                      : '0'
                                  : (buyAmountString === '0' ? '' : buyAmountString) || '0'
                        }
                        value=''
                        readOnly
                        onClick={() => {
                            if (
                                isBuyOutputLoading ||
                                isQuoteLoading ||
                                buyAmountString === '0' ||
                                buyAmountString === ''
                            ) {
                                return
                            }
                            // If you want to let user copy
                            onClickAmount && onClickAmount()
                        }}
                    />

                    {/* USD value with tooltip */}
                    <div className='flex items-center gap-1 text-base text-mach-sub-text'>
                        {((): JSX.Element => {
                            let elem: JSX.Element = <></>

                            if (isBitcoinAsset(buyData.token) && bitcoinPriceLoading) {
                                // Show loading state for Bitcoin price
                                elem = (
                                    <span className='inline-flex items-center gap-1'>
                                        <Loader2 className='h-3 w-3 animate-spin' />
                                        Loading price...
                                    </span>
                                )
                            } else if (isQuoteEnabled && isBuyOutputLoading && sellData.value > BigInt(0)) {
                                elem = <Skeleton containerClassName='w-16' enableAnimation baseColor='#9B9B9B' />
                            } else if (!exchangeQuoteEnabled || sellData.value === BigInt(0)) {
                                elem = <>$0</>
                            } else if (buyData.token.price_current === undefined && !isBitcoinAsset(buyData.token)) {
                                elem = <></>
                            } else {
                                elem = <>{buyBalanceInUSD}</>
                            }
                            return elem
                        })()}

                        {/* Solana tooltip */}
                        {isDestinationSolana &&
                            !isBuyOutputLoading &&
                            !isQuoteLoading &&
                            sellData.value > BigInt(0) &&
                            buyBalanceInUSD &&
                            buyBalanceInUSD !== '$0' && (
                                <>
                                    <span className='cursor-help text-gray-500' id='solana-token-account-tooltip'>
                                        <InfoIcon size={16} />
                                    </span>
                                    <Tooltip
                                        anchorId='solana-token-account-tooltip'
                                        place='top'
                                        className='z-[9999] max-w-xs rounded-md bg-gray-800 p-2 text-xs text-white shadow-lg'
                                        positionStrategy='fixed'
                                    >
                                        {SOLANA_TOKEN_ACCOUNT_TOOLTIP}
                                    </Tooltip>
                                </>
                            )}
                    </div>

                    {/* Change recipient toggle with transition */}
                    <div className='mt-3 flex items-center gap-2 whitespace-nowrap'>
                        <span className='flex-shrink-0 text-sm !leading-5 text-white'>Change recipient</span>
                        <Toggle
                            checked={isCustomRecipientWallet}
                            setChecked={onToggleCustomWallet}
                            className='flex-shrink-0 hover:cursor-pointer hover:opacity-80'
                            // Add custom transition classes if needed
                        />
                    </div>
                </div>

                {/* Token selection */}
                <div className='ml-2 flex-shrink-0 hover:cursor-pointer hover:opacity-80'>
                    <div
                        className='flex cursor-pointer flex-col items-end gap-y-2'
                        onClick={() => setIsTokenSelectorModalOpen(true)}
                    >
                        {/* Token symbol and icon row */}
                        <div className='flex items-center gap-2 py-1'>
                            {isConfigurationLoading ? (
                                <div className='h-7 w-7 animate-pulse rounded-full bg-mach-surface-border'></div>
                            ) : (
                                <img
                                    src={buyData.token.logoURI}
                                    alt={buyData.token.symbol}
                                    className='h-7 w-7 rounded-full'
                                    onError={e => {
                                        // Prevent infinite loop if fallback also fails
                                        if (e.currentTarget.src === '/assets/token/unknown.png') {
                                            return
                                        }

                                        // If we have a Solana token, try to fetch metadata directly
                                        if (buyData.chainId === ChainId.Solana) {
                                            getTokenMetadata(buyData.token.address)
                                                .then(metadata => {
                                                    if (metadata?.logoURI) {
                                                        e.currentTarget.src = metadata.logoURI
                                                    } else {
                                                        e.currentTarget.src = '/assets/token/unknown.png'
                                                    }
                                                })
                                                .catch(() => {
                                                    e.currentTarget.src = '/assets/token/unknown.png'
                                                })
                                        } else {
                                            e.currentTarget.src = '/assets/token/unknown.png'
                                        }
                                    }}
                                />
                            )}
                            <div className='flex items-center'>
                                {isConfigurationLoading ? (
                                    <div className='h-8 w-16 animate-pulse rounded-md bg-mach-surface-border'></div>
                                ) : (
                                    <span className='text-3xl font-semibold'>{buyData.token.symbol}</span>
                                )}

                                {/* Dropdown with dark background */}
                                <div className='ml-3 flex items-center justify-center rounded-lg bg-mach-gray p-[5px]'>
                                    <svg
                                        width='20'
                                        height='20'
                                        viewBox='0 0 24 24'
                                        fill='none'
                                        xmlns='http://www.w3.org/2000/svg'
                                    >
                                        <path
                                            d='M6 9L12 15L18 9'
                                            stroke='#ffffff'
                                            strokeWidth='2'
                                            strokeLinecap='round'
                                            strokeLinejoin='round'
                                        />
                                    </svg>
                                </div>
                            </div>
                        </div>

                        {/* Chain name row */}
                        <div className='flex items-center gap-1 text-base text-gray-400'>
                            <span className='mr-1 !leading-6'>on</span>
                            {/* Chain icon */}
                            <img
                                src={availableNetworks[buyData.chainId]?.icon || DEFAULT_NETWORK_ICONS[buyData.chainId]}
                                alt={`Chain ${buyData.chainId}`}
                                className='h-[14px] w-[14px] rounded-full text-white'
                                onError={e => {
                                    e.currentTarget.src = '/assets/token/unknown.png'
                                }}
                            />
                            <span className='!leading-6 text-white'>
                                {getChainName(getChainNameFromChainId(buyData.chainId))}
                            </span>
                        </div>
                    </div>

                    {/* Token selection modal */}
                    <TokenSelectorModal
                        isOpen={isTokenSelectorModalOpen}
                        onClose={() => setIsTokenSelectorModalOpen(false)}
                        selectedChain={buyData.chainId}
                        setSelectedChain={chainId => onTokenSelectionChange({ chainId: Number(chainId) }, 'buy')}
                        selectedToken={buyData.token}
                        setSelectedToken={token => {
                            onTokenSelectionChange({ token, chainId: token.chainId }, 'buy')
                            setIsTokenSelectorModalOpen(false)
                        }}
                        availableChains={availableNetworks}
                        disabledChains={disabledChains || []}
                        isSolanaSwap={buyData.chainId === ChainId.Solana || sellData.chainId === ChainId.Solana}
                        buyData={buyData}
                        sellData={sellData}
                        side='buy'
                    />
                </div>
            </div>

            {/* Change recipient input with smooth transition */}
            <div
                className='!mt-0 overflow-hidden'
                style={{
                    maxHeight: isCustomRecipientWallet ? '120px' : '0px',
                    opacity: isCustomRecipientWallet ? 1 : 0,
                    transition: 'max-height 300ms ease-in-out, opacity 200ms ease-in-out, margin-top 300ms ease-in-out',
                    // Opacity fades out faster when closing
                    transitionDuration: isCustomRecipientWallet ? '300ms, 200ms, 300ms' : '300ms, 100ms, 300ms',
                }}
            >
                <div>
                    <DestinationWalletInput
                        isVisible={true} // Always render, but control visibility with CSS
                        destinationAddress={destinationAddress}
                        isValidDestination={validateAddress(destinationAddress, buyData.chainId)}
                        chainName={getChainType(buyData.chainId)}
                        onChangeAddress={onChangeDestAddress}
                        chainId={buyData.chainId}
                        isResolvingSNS={isResolvingSNS}
                        resolvedSNSAddress={resolvedSNSAddress}
                        isResolvingENS={isResolvingENS}
                        resolvedENSAddress={resolvedENSAddress}
                    />
                </div>
            </div>

            {/* Trackers row */}
            {miles > 0 && !hasErrors && (
                <div className='mb-2 space-y-2'>
                    <div className='relative z-10 flex w-full flex-col gap-2 mobile-custom:flex-row'>
                        <MilesTracker miles={miles} tooltipPosition={isWiderThanMobile ? 'bottom' : 'top-left'} />
                        {sandDollars && isFeatureEnabled('SAND_DOLLARS') && (
                            <SandDollarsTracker
                                sandDollars={sandDollars}
                                isStablecoinSwap={isStablecoinSwap}
                                tooltipPosition={isWiderThanMobile ? 'bottom' : 'left'}
                            />
                        )}
                    </div>

                    {/* Stablecoin message */}
                    {shouldShowStablecoinMessage && (
                        <div className='animate-fade-in-up relative z-0 w-full text-sm font-medium leading-4 text-[#757575]'>
                            *Sand Dollars are not applied due to stablecoin swap
                        </div>
                    )}
                </div>
            )}
        </div>
    )
}
